import React, { Component } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import _isEqual from 'lodash/isEqual';

import { getTailwindColor } from '@trqk/utils';

import { unstyleScrollbar } from '../helpers';

interface Props {
    chartDivId: string;
    data: PerformanceChartDataElement[];
    dataFields: DataFields;
    height: number;
    numberFormat?: string;
    tooltipLabels: TooltipLabels;
}

const tertiary500 = am4core.color(getTailwindColor('tertiary-500'));
const zoomLevel = 10;

export class PerformanceBarPerContributionType extends Component<Props> {
    private chart: am4charts.XYChart | null = null;

    componentDidMount(): void {
        const { data, dataFields, chartDivId, numberFormat, tooltipLabels } =
            this.props;
        const chart = am4core.create(chartDivId, am4charts.XYChart);
        const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        const series = chart.series.push(new am4charts.ColumnSeries());

        chart.data = data;
        if (numberFormat) {
            chart.numberFormatter.numberFormat = numberFormat;
        }
        chart.fontSize = 12;
        chart.padding(20, 0, 0, 0);

        // categoryAxis.align = 'right';
        categoryAxis.dataFields.category = dataFields.category;
        categoryAxis.renderer.grid.template.disabled = true;
        categoryAxis.renderer.labels.template.fullWords = false;
        categoryAxis.renderer.labels.template.horizontalCenter = 'right';
        categoryAxis.renderer.labels.template.maxWidth = 120;
        categoryAxis.renderer.labels.template.rotation = -45;
        // categoryAxis.renderer.labels.template.align = 'right';
        categoryAxis.renderer.labels.template.fill = tertiary500;
        categoryAxis.renderer.labels.template.fullWords = false;
        // categoryAxis.renderer.labels.template.maxWidth = 160;
        categoryAxis.renderer.labels.template.tooltipX = am4core.percent(100);
        categoryAxis.renderer.labels.template.truncate = true;
        // Ensures that every category label is shown
        categoryAxis.renderer.minGridDistance = 0.01;

        valueAxis.renderer.labels.template.fill = tertiary500;
        valueAxis.renderer.maxLabelPosition = 0.95;

        // eslint-disable-next-line no-template-curly-in-string
        series.columns.template.tooltipText = `[bold]${tooltipLabels.category}[/]: {categoryX}\n[bold]${tooltipLabels.value}[/]: {valueY}`;
        series.dataFields.valueY = dataFields.value;
        series.dataFields.categoryX = dataFields.category;
        series.name = 'Earnings';

        // Set up scroll bars and zoom level per initial data array length.
        // If the array length changes, this solution will not work, as it runs only on initial
        // mount and refers to the length at that point.
        categoryAxis.maxZoomCount = zoomLevel;
        chart.zoomOutButton.disabled = true;

        if (chart.data.length <= zoomLevel) {
            categoryAxis.endLocation = 1 * (zoomLevel + 1 - chart.data.length);
        } else {
            chart.events.on('ready', (): void => {
                categoryAxis.zoomToIndexes(0, zoomLevel - 1);
            });
            chart.scrollbarY = new am4core.Scrollbar();
            unstyleScrollbar(chart.scrollbarY);
        }

        this.chart = chart;
    }

    componentDidUpdate(prevProps: Props): void {
        const { data } = this.props;

        if (!_isEqual(prevProps.data, data)) {
            if (this.chart !== null) {
                this.chart.data = data;
            }
        }
    }

    componentWillUnmount(): void {
        if (this.chart) {
            this.chart.dispose();
        }
    }

    render(): JSX.Element {
        const { chartDivId, height } = this.props;

        return (
            <div id={chartDivId} className="w-full pr-4" style={{ height }} />
        );
    }
}
